import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { getAuthHeaders } from '../../auth';
import Select2Dropdown from '../common/Select2Dropdown';
import { openFilePreview } from '../common/FilePreviewModal';
import useSortableTable from '../common/useSortableTable';
import TableScrollContainer from '../common/TableScrollContainer';

const API_URL = '/api/admin/payroll/';
const STAFF_URL = '/api/admin/staff/';
const EMAIL_TEMPLATE_URL = '/api/admin/email-templates/';

const formatMoney = (value) => {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const normalizeMoneyInput = (value) => {
    const cleaned = String(value ?? '').replace(/,/g, '').replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length <= 1) return cleaned;
    return `${parts[0]}.${parts.slice(1).join('')}`;
};

const stripMoneyFormatting = (value) => String(value ?? '').replace(/,/g, '');

const computeNhif = (staff) => {
    if (staff?.no_nhif) return 0;
    return Math.round(parseFloat(staff?.basic_salary || 0) * 0.03 * 100) / 100;
};

const computeGrossSalary = (staff) => {
    const basic = parseFloat(staff?.basic_salary || 0);
    const others = parseFloat(staff?.others || 0);
    const allowance = parseFloat(staff?.allowance || 0);
    const absent = parseFloat(staff?.absent_amount || 0);
    return Math.round((basic - absent + others + allowance) * 100) / 100;
};

const computeNssf = (staff) => {
    if (staff?.no_nssf) return 0;
    return Math.round(computeGrossSalary(staff) * 0.10 * 100) / 100;
};

const computeNetTaxableSalary = (staff) => Math.round((computeGrossSalary(staff) - computeNssf(staff)) * 100) / 100;

const computePaye = (staff) => {
    const taxableSalary = computeNetTaxableSalary(staff);
    if (taxableSalary > 1000000) return Math.round(((taxableSalary - 1000000) * 0.30 + 128000) * 100) / 100;
    if (taxableSalary > 760000) return Math.round(((taxableSalary - 760000) * 0.25 + 68000) * 100) / 100;
    if (taxableSalary > 520000) return Math.round(((taxableSalary - 520000) * 0.20 + 20000) * 100) / 100;
    if (taxableSalary > 270000) return Math.round((taxableSalary - 270000) * 0.08 * 100) / 100;
    return 0;
};

const computeTotal = (staff) => {
    const advance = parseFloat(staff.advance || 0);
    const loan = parseFloat(String(staff.loan || 0).replace(/,/g, ''));
    const nhif = computeNhif(staff);
    return Math.round((computeNetTaxableSalary(staff) - computePaye(staff) - nhif - advance - loan) * 100) / 100;
};

const formatDateTime = (value) => {
    if (!value) return 'Not sent';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return 'Not sent';
    return dt.toLocaleString();
};

const parseApiResponse = async (res) => {
    const contentType = res.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        return res.json();
    }

    const text = await res.text();
    return { detail: text || `Request failed with status ${res.status}` };
};

const Payroll = () => {
    const [activeTab, setActiveTab] = useState('GENERATE');
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [staffList, setStaffList] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [staffOptions, setStaffOptions] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [expandedSummary, setExpandedSummary] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailMode, setEmailMode] = useState('single');
    const [selectedPayrollForEmail, setSelectedPayrollForEmail] = useState(null);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [ccEmail, setCcEmail] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [bulkProgressOpen, setBulkProgressOpen] = useState(false);
    const [bulkStatuses, setBulkStatuses] = useState([]);

    const MONTH_OPTIONS = [
        { id: '', text: 'All Months' },
        { id: 1, text: 'January' }, { id: 2, text: 'February' }, { id: 3, text: 'March' },
        { id: 4, text: 'April' }, { id: 5, text: 'May' }, { id: 6, text: 'June' },
        { id: 7, text: 'July' }, { id: 8, text: 'August' }, { id: 9, text: 'September' },
        { id: 10, text: 'October' }, { id: 11, text: 'November' }, { id: 12, text: 'December' }
    ];

    const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
        const y = new Date().getFullYear() - 2 + i;
        return { id: y, text: y.toString() };
    });

    const fetchStaff = async () => {
        try {
            const res = await fetch(`${STAFF_URL}?page_size=1000`, { headers: getAuthHeaders() });
            if (res.ok) {
                const data = await res.json();
                const staff = data.results || data;
                setStaffOptions([{id: '', text: 'All Staff'}, ...staff.map(s => ({ id: s.id, text: `${s.first_name} ${s.last_name}` }))]);
            }
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        fetchStaff();
    }, []);

    const fetchPreparationData = async () => {
        if (!month) {
            toast.warning('Please select a month to generate payroll');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}prepare/?month=${month}&year=${year}`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch staff data');
            const data = await res.json();
            setStaffList(data);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            let query = `?year=${year}`;
            if (month) query += `&month=${month}`;
            if (selectedStaff) query += `&staff_id=${selectedStaff}`;

            const res = await fetch(`${API_URL}${query}`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to fetch history');
            const data = await res.json();
            setHistory(data);
            setExpandedSummary(false); // Reset expansion on new fetch
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    }, [month, year, selectedStaff]);

    const calculateSummary = () => {
        return history.reduce((acc, curr) => {
            acc.gross_salary += parseFloat(curr.gross_salary || 0);
            acc.paye += parseFloat(curr.paye || 0);
            acc.nssf += parseFloat(curr.nssf || 0);
            acc.nhif += parseFloat(curr.nhif || 0);
            acc.advance += parseFloat(curr.advance || 0);
            acc.loan += parseFloat(curr.loan || 0);
            acc.absent_amount += parseFloat(curr.absent_amount || 0);
            acc.total_deductions += parseFloat(curr.total_deductions || 0);
            acc.net_earning += parseFloat(curr.net_earning || 0);
            return acc;
        }, { gross_salary: 0, paye: 0, nssf: 0, nhif: 0, advance: 0, loan: 0, absent_amount: 0, total_deductions: 0, net_earning: 0 });
    };

    const handleBulkDownload = async () => {
        if (!month) {
            toast.warning('Please select a month for bulk download');
            return;
        }
        try {
            toast.info('Preparation started...');
            const res = await fetch(`${API_URL}bulk_download/?month=${month}&year=${year}`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to download payslips');
            
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Payslips_${month}_${year}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            toast.error(error.message);
        }
    };

    const loadEmailTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const res = await fetch(EMAIL_TEMPLATE_URL, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error('Failed to load email templates');
            const data = await res.json();
            setEmailTemplates(data || []);
        } catch (error) {
            toast.error(error.message);
            setEmailTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const openEmailPopup = async (mode, payrollItem = null) => {
        if (mode === 'bulk') {
            if (!month) {
                toast.warning('Please select a month for bulk email');
                return;
            }
            if (!history.length) {
                toast.warning('No payroll records found for selected filters');
                return;
            }
        }

        setEmailMode(mode);
        setSelectedPayrollForEmail(payrollItem);
        setSelectedTemplateId('');
        setCcEmail('');
        setEmailModalOpen(true);
        await loadEmailTemplates();
    };

    const getSelectedTemplate = () => emailTemplates.find(t => String(t.id) === String(selectedTemplateId));

    const getPopupRecipients = () => {
        if (emailMode === 'single') {
            if (!selectedPayrollForEmail) return [];
            return [{
                id: selectedPayrollForEmail.id,
                name: selectedPayrollForEmail.staff_name,
                email: selectedPayrollForEmail.staff_email || 'No email configured'
            }];
        }
        return history
            .filter(item => item.month == month && item.year == year)
            .map(item => ({
                id: item.id,
                name: item.staff_name,
                email: item.staff_email || 'No email configured'
            }));
    };

    const sendPayslipEmail = async (payrollId, payload) => {
        const res = await fetch(`${API_URL}${payrollId}/send-email/`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || data.detail || 'Failed to send payslip email');
        }
        return data;
    };

    const handleConfirmSendEmail = async () => {
        if (!selectedTemplateId) {
            toast.warning('Please select an email template');
            return;
        }

        setSendingEmail(true);
        try {
            if (emailMode === 'single') {
                if (!selectedPayrollForEmail) {
                    throw new Error('No payroll selected');
                }

                await sendPayslipEmail(selectedPayrollForEmail.id, {
                    template_id: selectedTemplateId,
                    cc_email: ccEmail,
                });

                toast.success(`Payslip email sent to ${selectedPayrollForEmail.staff_name}`);
                setEmailModalOpen(false);
                await fetchHistory();
                return;
            }

            const targets = history.filter(item => item.month == month && item.year == year);
            if (!targets.length) {
                throw new Error('No payroll records found for selected month/year');
            }

            setBulkStatuses(targets.map(item => ({
                payrollId: item.id,
                staffName: item.staff_name,
                status: 'Queued',
                message: '',
                sentAt: null,
            })));
            setBulkProgressOpen(true);

            for (const item of targets) {
                setBulkStatuses(prev => prev.map(row => row.payrollId === item.id
                    ? { ...row, status: 'Sending', message: 'Sending in progress...' }
                    : row));

                try {
                    const result = await sendPayslipEmail(item.id, {
                        template_id: selectedTemplateId,
                        cc_email: ccEmail,
                    });

                    setBulkStatuses(prev => prev.map(row => row.payrollId === item.id
                        ? { ...row, status: 'Sent', message: result.message || 'Email sent', sentAt: result.payslip_emailed_at || null }
                        : row));
                } catch (error) {
                    setBulkStatuses(prev => prev.map(row => row.payrollId === item.id
                        ? { ...row, status: 'Failed', message: error.message }
                        : row));
                }
            }

            setEmailModalOpen(false);
            await fetchHistory();
            toast.success('Bulk payslip email process finished');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSendingEmail(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'HISTORY') {
            fetchHistory();
        }
    }, [activeTab, fetchHistory]);

    const handleInputChange = (index, field, value) => {
        const updated = [...staffList];
        const normalizedValue = normalizeMoneyInput(value);
        if (field === 'loan') {
            const entered = Number(stripMoneyFormatting(normalizedValue || 0));
            const maxEffective = Number(stripMoneyFormatting(updated[index].max_effective_loan || 0));
            const clamped = Number.isFinite(entered) ? Math.min(entered, maxEffective) : 0;
            updated[index][field] = String(clamped);
        } else {
            updated[index][field] = normalizedValue;
        }
        setStaffList(updated);
    };

    const handleMoneyFocus = (index, field) => {
        const updated = [...staffList];
        updated[index][field] = stripMoneyFormatting(updated[index][field]);
        setStaffList(updated);
    };

    const handleMoneyBlur = (index, field) => {
        const updated = [...staffList];
        if (field === 'loan') {
            const entered = Number(stripMoneyFormatting(updated[index][field] || 0));
            const maxEffective = Number(stripMoneyFormatting(updated[index].max_effective_loan || 0));
            const clamped = Number.isFinite(entered) ? Math.min(entered, maxEffective) : 0;
            updated[index][field] = formatMoney(clamped);
            setStaffList(updated);
            return;
        }
        updated[index][field] = formatMoney(updated[index][field] || 0);
        setStaffList(updated);
    };

    const historySortValue = useCallback((item, key) => {
        if (key === 'month') {
            const yearValue = String(item.year || '');
            const monthValue = String(item.month || '').padStart(2, '0');
            return `${yearValue}-${monthValue}`;
        }
        return item[key];
    }, []);

    const {
        items: sortedStaffList,
        requestSort: requestGenerateSort,
        getSortIndicator: getGenerateSortIndicator
    } = useSortableTable(staffList, { key: 'staff_name', direction: 'ascending' });

    const handleGenerateExcel = useCallback(() => {
        if (!sortedStaffList.length) {
            toast.warning('No staff data to export. Load staff first.');
            return;
        }

        const monthText = MONTH_OPTIONS.find(m => m.id == month)?.text || month;
        const title = `PAYROLL ${monthText} ${year}`;
        const headers = ['SN', 'Name', 'Basic', 'Absent Amount', 'Others', 'Allowance', 'Gross Salary', 'NSSF (Deduct)', 'Net Taxable Salary', 'PAYE (Deduct)', 'NHIF 3%', 'Advance', 'Loan', 'Net Salary'];
        const colCount = headers.length;

        const dataRows = sortedStaffList.map((staff, i) => {
            const basic = parseFloat(staff.basic_salary || 0);
            const absent = parseFloat(staff.absent_amount || 0);
            const others = parseFloat(staff.others || 0);
            const allowance = parseFloat(staff.allowance || 0);
            const gross = computeGrossSalary(staff);
            const nssf = computeNssf(staff);
            const netTaxableSalary = computeNetTaxableSalary(staff);
            const paye = computePaye(staff);
            const nhif = computeNhif(staff);
            const advance = parseFloat(staff.advance || 0);
            const loan = parseFloat(String(staff.loan || 0).replace(/,/g, ''));
            const total = computeTotal(staff);
            return [i + 1, staff.staff_name, basic, absent, others, allowance, gross, nssf, netTaxableSalary, paye, nhif, advance, loan, total];
        });

        const totals = dataRows.reduce((acc, row) => {
            for (let i = 2; i < colCount; i++) {
                acc[i - 2] = Math.round((acc[i - 2] + row[i]) * 100) / 100;
            }
            return acc;
        }, new Array(colCount - 2).fill(0));

        const totalRow = ['TOTALS', '', ...totals];
        const aoa = [[title], headers, ...dataRows, totalRow];

        const ws = XLSX.utils.aoa_to_sheet(aoa);

        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } }];
        ws['!rows'] = [{ hpt: 24 }];
        ws['!cols'] = headers.map((header, columnIndex) => {
            const values = dataRows.map(row => String(row[columnIndex] ?? ''));
            const widestValue = Math.max(header.length, ...values.map(value => value.length), columnIndex === 1 ? 20 : 12);
            return { wch: Math.min(widestValue + 2, columnIndex === 1 ? 35 : 24) };
        });

        const borderAll = {
            top: { style: 'thin', color: { rgb: '000000' } },
            bottom: { style: 'thin', color: { rgb: '000000' } },
            left: { style: 'thin', color: { rgb: '000000' } },
            right: { style: 'thin', color: { rgb: '000000' } }
        };

        const titleStyle = {
            font: { bold: true, color: { rgb: 'FF0000' }, sz: 14, name: 'Arial' },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: borderAll
        };
        const headerStyle = {
            font: { bold: true, color: { rgb: '000000' } },
            alignment: { horizontal: 'center' },
            border: borderAll
        };
        const dataStyle = { border: borderAll };
        const totalStyle = {
            font: { bold: true, color: { rgb: '000000' } },
            border: borderAll
        };
        const accountingNumberFormat = '_(* #,##0.00_);_(* (#,##0.00);_(* "-"??_);_(@_)';

        for (let c = 0; c < colCount; c++) {
            const titleCell = ws[XLSX.utils.encode_cell({ r: 0, c })];
            if (titleCell) titleCell.s = titleStyle;

            const headerCell = ws[XLSX.utils.encode_cell({ r: 1, c })];
            if (headerCell) headerCell.s = headerStyle;

            for (let r = 0; r < dataRows.length; r++) {
                const cell = ws[XLSX.utils.encode_cell({ r: r + 2, c })];
                if (cell) {
                    cell.s = dataStyle;
                    if (c >= 2) cell.z = accountingNumberFormat;
                }
            }

            const totalCellRef = XLSX.utils.encode_cell({ r: dataRows.length + 2, c });
            const totalCell = ws[totalCellRef];
            if (totalCell) {
                totalCell.s = totalStyle;
                if (c >= 2) totalCell.z = accountingNumberFormat;
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Payroll');
        XLSX.writeFile(wb, `Payroll_${monthText}_${year}.xlsx`);
    }, [sortedStaffList, month, year]);

    const {
        items: sortedHistory,
        requestSort: requestHistorySort,
        getSortIndicator: getHistorySortIndicator
    } = useSortableTable(history, { key: 'staff_name', direction: 'ascending' }, historySortValue);

    const handleGenerate = async () => {
        if (!window.confirm('Are you sure you want to generate payroll for the selected month? This will overwrite existing records.')) return;
        
        setLoading(true);
        try {
            const payload = {
                month,
                year,
                staff_list: staffList
            };
            const res = await fetch(`${API_URL}generate/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            const data = await parseApiResponse(res);
            if (res.ok) {
                if ((data.generated_count || 0) > 0) {
                    toast.success(data.message || 'Payroll generated successfully');
                }
                if (data.errors && data.errors.length > 0) {
                    toast.warning(`Some errors occurred: ${data.errors.join(', ')}`);
                }
                if ((data.generated_count || 0) > 0) {
                    setActiveTab('HISTORY');
                }
            } else {
                throw new Error(data.detail || 'Failed to generate payroll');
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateSingle = async (staff) => {
        if (!window.confirm(`Generate payroll for ${staff.staff_name}?`)) return;

        setLoading(true);
        try {
            const payload = {
                month,
                year,
                staff_list: [staff]
            };
            const res = await fetch(`${API_URL}generate/`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(payload)
            });
            const data = await parseApiResponse(res);
            if (res.ok) {
                if ((data.generated_count || 0) > 0) {
                    toast.success(`Payroll generated for ${staff.staff_name}`);
                    // Refresh the list to show updated status
                    fetchPreparationData();
                }
                if (data.errors && data.errors.length > 0) {
                    toast.warning(`Error: ${data.errors.join(', ')}`);
                }
            } else {
                throw new Error(data.detail || 'Failed to generate payroll');
            }
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-container">
            <style>{`
                .payroll-table thead th {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    background-color: #f8f9fa;
                }
            `}</style>
            <div className="admin-header">
                <h2>Payroll Management</h2>
            </div>

            <div className="custom-tabs-container" style={{ marginTop: 16, marginBottom: 16 }}>
                <button className={`custom-tab ${activeTab === 'GENERATE' ? 'active' : ''}`} onClick={() => setActiveTab('GENERATE')}>Generate Payroll</button>
                <button className={`custom-tab ${activeTab === 'HISTORY' ? 'active' : ''}`} onClick={() => setActiveTab('HISTORY')}>Payroll History</button>
            </div>

            <div className="filters-container" style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ width: 150 }}>
                    <label>Month</label>
                    <Select2Dropdown 
                        options={MONTH_OPTIONS} 
                        value={month} 
                        onChange={setMonth} 
                        displayKey="text"
                        valueKey="id"
                        placeholder="All Months"
                    />
                </div>
                <div style={{ width: 100 }}>
                    <label>Year</label>
                    <Select2Dropdown 
                        options={YEAR_OPTIONS} 
                        value={year} 
                        onChange={setYear} 
                        displayKey="text"
                        valueKey="id"
                    />
                </div>
                
                {activeTab === 'HISTORY' && (
                    <div style={{ width: 250 }}>
                        <label>Staff</label>
                        <Select2Dropdown 
                            options={staffOptions} 
                            value={selectedStaff} 
                            onChange={setSelectedStaff} 
                            displayKey="text"
                            valueKey="id"
                            placeholder="All Staff"
                        />
                    </div>
                )}

                {activeTab === 'GENERATE' && (
                    <button className="btn btn-primary" onClick={fetchPreparationData} style={{ marginTop: 24 }}>
                        Load Staff
                    </button>
                )}
                {activeTab === 'GENERATE' && staffList.length > 0 && (
                    <button
                        className="btn"
                        onClick={handleGenerateExcel}
                        style={{ marginTop: 24, marginLeft: 'auto', backgroundColor: '#28a745', color: '#fff', border: '1px solid #28a745' }}
                    >
                        Generate Excel
                    </button>
                )}
                {activeTab === 'HISTORY' && (
                    <>
                        <button
                            onClick={handleBulkDownload}
                            className="btn btn-secondary"
                            style={{ marginTop: 24 }}
                        >
                            <i className="fa fa-download"></i> Bulk Download Payslips
                        </button>
                        <button
                            onClick={() => openEmailPopup('bulk')}
                            className="btn btn-primary"
                            style={{ marginTop: 24 }}
                        >
                            <i className="fa fa-envelope"></i> Bulk Email
                        </button>
                    </>
                )}
            </div>

            {loading && <p>Loading...</p>}

            {activeTab === 'GENERATE' && !loading && staffList.length > 0 && (
                <div>
                    <TableScrollContainer>
                        <table className="admin-table payroll-table">
                            <thead>
                                <tr>
                                    <th style={{ cursor: 'pointer' }} onClick={() => requestGenerateSort('staff_name')}>Staff Name {getGenerateSortIndicator('staff_name')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => requestGenerateSort('basic_salary')}>Basic Salary {getGenerateSortIndicator('basic_salary')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => requestGenerateSort('absent_amount')}>Absent Amount {getGenerateSortIndicator('absent_amount')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => requestGenerateSort('others')}>Others {getGenerateSortIndicator('others')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => requestGenerateSort('allowance')}>Allowance {getGenerateSortIndicator('allowance')}</th>
                                    <th style={{ cursor: 'pointer' }}>Gross Salary</th>
                                    <th style={{ cursor: 'pointer' }}>NSSF (Deduct)</th>
                                    <th style={{ cursor: 'pointer' }}>Net Taxable Salary</th>
                                    <th style={{ cursor: 'pointer' }}>PAYE (Deduct)</th>
                                    <th style={{ cursor: 'pointer' }}>NHIF (Deduct)</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => requestGenerateSort('advance')}>Advance (Deduct) {getGenerateSortIndicator('advance')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => requestGenerateSort('loan')}>Loan (Deduct) {getGenerateSortIndicator('loan')}</th>
                                    <th style={{ cursor: 'pointer' }} onClick={() => requestGenerateSort('absent_days')}>Absent Days {getGenerateSortIndicator('absent_days')}</th>
                                    <th style={{ cursor: 'pointer' }}>Total</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedStaffList.map((staff) => {
                                    const index = staffList.indexOf(staff);
                                    return (
                                    <tr key={staff.staff_id}>
                                        <td>{staff.staff_name}</td>
                                        <td>{formatMoney(staff.basic_salary)}</td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formatMoney(staff.absent_amount || 0)}
                                                readOnly
                                            />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(staff.others)} readOnly />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(staff.allowance || 0)} readOnly />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(computeGrossSalary(staff))} readOnly />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(computeNssf(staff))} readOnly />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(computeNetTaxableSalary(staff))} readOnly />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(computePaye(staff))} readOnly />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(computeNhif(staff))} readOnly />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(staff.advance)} readOnly />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={staff.loan ?? ''}
                                                onChange={e => handleInputChange(index, 'loan', e.target.value)}
                                                onFocus={() => handleMoneyFocus(index, 'loan')}
                                                onBlur={() => handleMoneyBlur(index, 'loan')}
                                                title={`Max deductible for this payroll period: ${formatMoney(staff.max_effective_loan || 0)}`}
                                            />
                                        </td>
                                        <td>
                                            <input type="number" className="form-control" value={staff.absent_days} readOnly />
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" value={formatMoney(computeTotal(staff))} readOnly />
                                        </td>
                                        <td>
                                            {staff.already_generated ? <span className="badge badge-success">Generated</span> : <span className="badge badge-warning">Pending</span>}
                                        </td>
                                        <td>
                                            <button className="btn btn-sm btn-primary" onClick={() => handleGenerateSingle(staff)}>Generate</button>
                                        </td>
                                    </tr>
                                    );
                                })}
                                {staffList.length > 0 && (
                                    <tr style={{ backgroundColor: '#f0f4f8', fontWeight: 600 }}>
                                        <td>Total</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + parseFloat(x.basic_salary || 0), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + parseFloat(x.absent_amount || 0), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + parseFloat(x.others || 0), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + parseFloat(x.allowance || 0), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + computeGrossSalary(x), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + computeNssf(x), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + computeNetTaxableSalary(x), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + computePaye(x), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + computeNhif(x), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + parseFloat(x.advance || 0), 0))}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + parseFloat(String(x.loan || 0).replace(/,/g, '')), 0))}</td>
                                        <td>{staffList.reduce((s, x) => s + Number(x.absent_days || 0), 0)}</td>
                                        <td>{formatMoney(staffList.reduce((s, x) => s + computeTotal(x), 0))}</td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </TableScrollContainer>
                    <div style={{ marginTop: 20, textAlign: 'right' }}>
                        <button className="btn btn-primary" onClick={handleGenerate}>Generate All Payrolls</button>
                    </div>
                </div>
            )}

            {activeTab === 'HISTORY' && !loading && (
                <TableScrollContainer>
                    <table className="admin-table payroll-table">
                        <thead>
                            <tr>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('staff_name')}>Staff Name {getHistorySortIndicator('staff_name')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('month')}>Month/Year {getHistorySortIndicator('month')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('gross_salary')}>Gross Salary {getHistorySortIndicator('gross_salary')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('paye')}>PAYE {getHistorySortIndicator('paye')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('nssf')}>NSSF {getHistorySortIndicator('nssf')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('nhif')}>NHIF {getHistorySortIndicator('nhif')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('advance')}>Advance {getHistorySortIndicator('advance')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('loan')}>Loan {getHistorySortIndicator('loan')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('absent_amount')}>Absent Amount {getHistorySortIndicator('absent_amount')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => requestHistorySort('net_earning')}>Net Earning {getHistorySortIndicator('net_earning')}</th>
                                <th>Email Sent</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length === 0 && <tr><td colSpan={12} className="no-data">No records found.</td></tr>}
                            
                            {/* Summary Row */}
                            {history.length > 0 && month && year && !selectedStaff && (
                                <>
                                    <tr 
                                        style={{ backgroundColor: '#e2f0fb', fontWeight: 'bold', cursor: 'pointer' }}
                                        onClick={() => setExpandedSummary(!expandedSummary)}
                                    >
                                        <td>
                                            <i className={`fa fa-chevron-${expandedSummary ? 'down' : 'right'}`} style={{ marginRight: 8 }}></i>
                                            Period Summary
                                        </td>
                                        <td>{MONTH_OPTIONS.find(m => m.id == month)?.text} {year}</td>
                                        <td>{formatMoney(calculateSummary().gross_salary)}</td>
                                        <td>{formatMoney(calculateSummary().paye)}</td>
                                        <td>{formatMoney(calculateSummary().nssf)}</td>
                                        <td>{formatMoney(calculateSummary().nhif)}</td>
                                        <td>{formatMoney(calculateSummary().advance)}</td>
                                        <td>{formatMoney(calculateSummary().loan)}</td>
                                        <td>{formatMoney(calculateSummary().absent_amount)}</td>
                                        <td>{formatMoney(calculateSummary().net_earning)}</td>
                                        <td>-</td>
                                        <td>
                                            {/* Could add bulk download btn here later if desired */}
                                        </td>
                                    </tr>
                                </>
                            )}
                            
                            {/* Individual Rows - Show if expanded OR if user has filtered by specific staff/view */}
                            {(!month || !year || selectedStaff || expandedSummary) && sortedHistory.map(item => (
                                <tr key={item.id}>
                                    <td>{item.staff_name}</td>
                                    <td>{item.month_display} {item.year}</td>
                                    <td>{formatMoney(item.gross_salary)}</td>
                                    <td>{formatMoney(item.paye)}</td>
                                    <td>{formatMoney(item.nssf)}</td>
                                    <td>{formatMoney(item.nhif)}</td>
                                    <td>{formatMoney(item.advance)}</td>
                                    <td>{formatMoney(item.loan)}</td>
                                    <td>{formatMoney(item.absent_amount || 0)}</td>
                                    <td>{formatMoney(item.net_earning)}</td>
                                    <td>{formatDateTime(item.payslip_emailed_at)}</td>
                                    <td>
                                        {item.pdf_file && (
                                            <button 
                                                className="btn btn-sm btn-info"
                                                onClick={() => openFilePreview(item.pdf_file, `Payslip_${item.staff_name}.pdf`)}
                                            >
                                                <i className="fa fa-file-pdf"></i> Payslip
                                            </button>
                                        )}
                                        <button
                                            className="btn btn-sm btn-primary"
                                            style={{ marginLeft: 8 }}
                                            onClick={() => openEmailPopup('single', item)}
                                        >
                                            <i className="fa fa-envelope"></i> Send Email
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </TableScrollContainer>
            )}

            {emailModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                }}>
                    <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: 20, width: 'min(520px, 92vw)' }}>
                        <h4 style={{ marginTop: 0 }}>
                            {emailMode === 'single' ? 'Send Payslip Email' : 'Bulk Payslip Email'}
                        </h4>
                        <div style={{ marginBottom: 12 }}>
                            <label>Template</label>
                            <select
                                className="form-control"
                                value={selectedTemplateId}
                                onChange={(e) => {
                                    const nextTemplateId = e.target.value;
                                    setSelectedTemplateId(nextTemplateId);
                                    const chosen = emailTemplates.find(t => String(t.id) === String(nextTemplateId));
                                    setCcEmail(chosen?.cc_emails || '');
                                }}
                                disabled={loadingTemplates || sendingEmail}
                            >
                                <option value="">Select template</option>
                                {emailTemplates.map(template => (
                                    <option key={template.id} value={template.id}>{template.template_name}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                            <label>CC Email Address(es)</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Auto from template configuration"
                                value={ccEmail}
                                readOnly
                                disabled={sendingEmail}
                            />
                        </div>
                        <div style={{ marginBottom: 16, color: '#555' }}>
                            {emailMode === 'single' ? (
                                <>
                                    <p style={{ marginBottom: 8 }}>Recipient: {selectedPayrollForEmail?.staff_name || '-'}</p>
                                    <p style={{ marginBottom: 0 }}>Recipient Email: {selectedPayrollForEmail?.staff_email || 'No email configured'}</p>
                                </>
                            ) : (
                                <>
                                    <p style={{ marginBottom: 8 }}>Recipients: {getPopupRecipients().length} staff</p>
                                    <div style={{ maxHeight: 170, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 6, padding: 8, backgroundColor: '#fafafa' }}>
                                        {getPopupRecipients().map((recipient) => (
                                            <div key={recipient.id} style={{ fontSize: 13, marginBottom: 4 }}>
                                                {recipient.name} - {recipient.email}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setEmailModalOpen(false)}
                                disabled={sendingEmail}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleConfirmSendEmail}
                                disabled={sendingEmail}
                            >
                                {sendingEmail ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {bulkProgressOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.45)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                }}>
                    <div style={{ backgroundColor: '#fff', borderRadius: 8, padding: 20, width: 'min(760px, 94vw)', maxHeight: '80vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                            <h4 style={{ margin: 0 }}>Bulk Email Status</h4>
                            <button className="btn btn-sm btn-secondary" onClick={() => setBulkProgressOpen(false)} disabled={sendingEmail}>Close</button>
                        </div>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Staff</th>
                                    <th>Status</th>
                                    <th>Message</th>
                                    <th>Sent At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bulkStatuses.length === 0 && <tr><td colSpan={4}>No status yet.</td></tr>}
                                {bulkStatuses.map(row => (
                                    <tr key={row.payrollId}>
                                        <td>{row.staffName}</td>
                                        <td>{row.status}</td>
                                        <td>{row.message || '-'}</td>
                                        <td>{formatDateTime(row.sentAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Payroll;
